import Phaser from "phaser";

import { MinigameScene, type PanelCopy } from "./MinigameScene";
import { CloudLayer, SpeckleLayer } from "./Scenery";

// Campo de jogo em resolucao baixa; a camera aplica o zoom por cima, que e o
// que mantem o pixel art nitido em qualquer tamanho de janela. O que sobra de
// tela alem do campo vira ceu, cidade e chao (ver layoutView).
const VIEW_W = 200;
const VIEW_H = 150;
const GROUND_Y = 138;
/** Altura da faixa de chao desenhada com textura; abaixo dela e terra lisa. */
const GROUND_H = 16;
/** Altura do desenho da cidade ao fundo, que se apoia no chao. */
const SKYLINE_H = 64;

const SKY_COLOR = 0x6fa8d8;
/** Mesma cor do corpo da textura do chao: a terra continua sem emenda. */
const DIRT_COLOR = 0xc4ac6c;
const PEBBLE_COLOR = 0xa8925a;

const GRAVITY = 460;
const FLAP_VELOCITY = -148;
const MAX_FALL = 220;

const PIPE_WIDTH = 32;
const PIPE_GAP = 50;
const PIPE_SPACING = 92;
const PIPE_SPEED = 62;
const BIRD_X = 58;
const BIRD_HALF = 5;
const BIRD_START_Y = 70;
/**
 * Distancia entre o passaro e a borda direita da tela. E ela que define quanto
 * tempo o jogador tem para reagir a um cano, entao e ela — e nao o X do
 * passaro — que precisa ser igual em toda tela: em tela larga o passaro anda
 * para a direita e a sobra fica atras dele, mostrando os canos ja passados.
 */
const BIRD_REACH = VIEW_W - BIRD_X;
/**
 * Quanto o passaro pode subir acima do campo. Fixo de proposito: se o teto
 * acompanhasse o alto da tela, a tela alta daria muito mais espaco para errar
 * que a larga, e o ranking e o mesmo para todo mundo. O maior gap possivel
 * comeca em y=13, entao o teto nunca atrapalha a passagem por um cano.
 */
const CEILING_HEADROOM = 24;

interface Pipe {
  x: number;
  gapY: number;
  scored: boolean;
  topBody: Phaser.GameObjects.TileSprite;
  topCap: Phaser.GameObjects.Image;
  bottomBody: Phaser.GameObjects.TileSprite;
  bottomCap: Phaser.GameObjects.Image;
}

/**
 * Minigame estilo Flappy Bird. Roda sobre a cidade (que fica pausada) e envia
 * a pontuacao para o ranking ao final de cada partida.
 */
export class FlappyScene extends MinigameScene {
  private velocity = 0;
  private birdY = BIRD_START_Y;
  /** Anda com a borda direita da tela; ver BIRD_REACH. */
  private birdX = BIRD_X;

  private pipes: Pipe[] = [];
  private nextPipeX = VIEW_W + 40;

  private bird!: Phaser.GameObjects.Sprite;
  private sky!: Phaser.GameObjects.Rectangle;
  private skyline!: Phaser.GameObjects.TileSprite;
  private ground!: Phaser.GameObjects.TileSprite;
  private dirt!: Phaser.GameObjects.Rectangle;
  private clouds!: CloudLayer;
  private pebbles!: SpeckleLayer;

  constructor() {
    super("flappy", VIEW_W, VIEW_H);
  }

  // ------------------------------------------------------------- montagem

  protected buildWorld(): void {
    // O shutdown da cena destroi os canos, mas nao esvazia a lista: o Phaser
    // reaproveita a instancia a cada `launch`.
    this.pipes = [];

    // Ceu e nuvens ficam fora do container: la a ordem de desenho e a ordem de
    // insercao, e os canos entram depois. Aqui o depth resolve.
    this.sky = this.add.rectangle(0, 0, VIEW_W, VIEW_H, SKY_COLOR).setOrigin(0, 0).setDepth(-20);
    // A nuvem do corredor e um desenho neutro e serve aos dois jogos.
    this.clouds = new CloudLayer(this, "runner-cloud", -19, 1);

    this.skyline = this.add
      .tileSprite(0, GROUND_Y - SKYLINE_H, VIEW_W, SKYLINE_H, "flappy-skyline")
      .setOrigin(0, 0);
    this.ground = this.add.tileSprite(0, GROUND_Y, VIEW_W, GROUND_H, "flappy-ground").setOrigin(0, 0);
    // Abaixo da faixa com textura a terra segue lisa ate o fim da tela: repetir
    // a textura na vertical deixaria linhas escuras empilhadas.
    this.dirt = this.add.rectangle(0, GROUND_Y + GROUND_H, VIEW_W, 1, DIRT_COLOR).setOrigin(0, 0);
    this.bird = this.add.sprite(this.birdX, this.birdY, "flappy-bird", 0).play("flappy-flap");

    this.field.add([this.skyline, this.ground, this.dirt, this.bird]);
    this.pebbles = new SpeckleLayer(this, {
      depth: 1,
      width: 2,
      height: 1,
      color: PEBBLE_COLOR,
      density: 4,
      max: 60,
    });
  }

  /**
   * Estica o cenario ate as bordas da tela. O campo continua do mesmo tamanho:
   * a sobra vira ceu (acima), terra (abaixo) e canos ja passados (a esquerda).
   */
  protected override layoutView(): void {
    const view = this.view;

    this.sky.setPosition(view.left, view.top).setSize(view.width, view.height);
    this.skyline.setPosition(view.left, GROUND_Y - SKYLINE_H).setSize(view.width, SKYLINE_H);
    this.ground.setPosition(view.left, GROUND_Y).setSize(view.width, GROUND_H);
    // A faixa com textura ja passa um pouco do campo: so ha terra lisa a
    // desenhar quando a tela sobra abaixo dela.
    const dirtTop = GROUND_Y + GROUND_H;
    this.dirt.setPosition(view.left, dirtTop).setSize(view.width, Math.max(0, view.bottom - dirtTop));

    this.clouds.fit({
      left: view.left,
      right: view.right,
      top: view.top + 8,
      bottom: GROUND_Y - SKYLINE_H - 10,
    });
    this.pebbles.fit({ left: view.left, right: view.right, top: dirtTop + 2, bottom: view.bottom });

    // A tela mudou de largura: o passaro anda junto com a borda direita, e os
    // canos vao junto para que a partida em andamento nao mude de figura.
    const birdX = view.right - BIRD_REACH;
    const shift = birdX - this.birdX;
    this.birdX = birdX;
    this.bird.setX(birdX);
    this.nextPipeX += shift;
    for (const pipe of this.pipes) {
      pipe.x += shift;
      this.layoutPipe(pipe);
    }
  }

  protected introPanel(): PanelCopy {
    return {
      text: `${this.params.label.toUpperCase()}\n\nDesvie dos canos.`,
      hint: this.params.touch.enabled
        ? "Toque na tela para voar\n'Voltar' encerra a partida"
        : "ESPAÇO ou clique para voar\nESC volta para a cidade",
    };
  }

  // ------------------------------------------------------------- gameplay

  protected resetRun(): void {
    for (const pipe of this.pipes) this.destroyPipe(pipe);
    this.pipes = [];
    this.nextPipeX = this.view.right + 40;
    this.birdY = BIRD_START_Y;
    this.velocity = 0;
    this.bird.setAngle(0);
  }

  protected action(): void {
    this.velocity = FLAP_VELOCITY;
  }

  protected advance(dt: number): void {
    const shift = PIPE_SPEED * dt;
    this.skyline.tilePositionX += shift * 0.25;
    this.ground.tilePositionX += shift;
    this.clouds.scroll(shift);
    this.pebbles.scroll(shift);

    this.velocity = Math.min(this.velocity + GRAVITY * dt, MAX_FALL);
    this.birdY += this.velocity * dt;

    // O teto limita, mas nao mata: quem perde e quem bate em cano ou no chao.
    // Ele passa do alto do campo para o ceu nao virar uma parede logo acima do
    // cano mais alto, mas so ate CEILING_HEADROOM: o resto da sobra de tela e
    // cenario, e nao espaco de manobra que a tela alta ganharia de graca.
    const ceiling = Math.max(this.view.top, -CEILING_HEADROOM) + BIRD_HALF;
    if (this.birdY < ceiling) {
      this.birdY = ceiling;
      this.velocity = Math.max(this.velocity, 0);
    }

    this.movePipes(dt);
    this.spawnPipes();
    this.syncBird();

    if (this.checkCollisions()) void this.fail();
  }

  protected override idle(dt: number): void {
    if (this.phase === "ready") {
      // flutuacao suave enquanto espera o primeiro comando
      this.birdY = BIRD_START_Y + Math.sin(this.time.now / 240) * 3;
      this.skyline.tilePositionX += 4 * dt;
      this.clouds.scroll(16 * dt);
    }
    this.syncBird();
  }

  private syncBird(): void {
    this.bird.setY(Math.round(this.birdY));
    this.bird.setAngle(Phaser.Math.Clamp(this.velocity * 0.12, -25, 70));
  }

  private movePipes(dt: number): void {
    const shift = PIPE_SPEED * dt;
    this.nextPipeX -= shift;

    for (const pipe of this.pipes) {
      pipe.x -= shift;
      this.layoutPipe(pipe);

      if (!pipe.scored && pipe.x + PIPE_WIDTH / 2 < this.birdX - BIRD_HALF) {
        pipe.scored = true;
        this.addScore();
      }
    }

    const survivors: Pipe[] = [];
    for (const pipe of this.pipes) {
      if (pipe.x < this.view.left - PIPE_WIDTH) this.destroyPipe(pipe);
      else survivors.push(pipe);
    }
    this.pipes = survivors;
  }

  private spawnPipes(): void {
    // O vao entre canos e sempre o mesmo; o que muda com a tela e so quando o
    // cano aparece — e ele ja nasce fora da vista, seja ela larga ou estreita.
    while (this.nextPipeX < this.view.right + PIPE_WIDTH) {
      const gapY = Phaser.Math.Between(38, GROUND_Y - 34);
      this.pipes.push(this.createPipe(this.nextPipeX, gapY));
      this.nextPipeX += PIPE_SPACING;
    }
  }

  private createPipe(x: number, gapY: number): Pipe {
    const topBody = this.add.tileSprite(x, 0, PIPE_WIDTH, 1, "flappy-pipe-body").setOrigin(0.5, 0);
    const topCap = this.add.image(x, 0, "flappy-pipe-cap").setOrigin(0.5, 1);
    const bottomBody = this.add.tileSprite(x, 0, PIPE_WIDTH, 1, "flappy-pipe-body").setOrigin(0.5, 0);
    const bottomCap = this.add.image(x, 0, "flappy-pipe-cap").setOrigin(0.5, 0);

    this.field.add([topBody, topCap, bottomBody, bottomCap]);
    // Os canos ficam atras do chao e do passaro.
    this.field.moveTo(topBody, 1);
    this.field.moveTo(topCap, 2);
    this.field.moveTo(bottomBody, 3);
    this.field.moveTo(bottomCap, 4);

    const pipe: Pipe = { x, gapY, scored: false, topBody, topCap, bottomBody, bottomCap };
    this.layoutPipe(pipe);
    return pipe;
  }

  /**
   * Posiciona o cano. O vao e sempre o mesmo; o que estica e o corpo, que vai
   * do alto da tela ate o vao e do vao ate o chao — assim nao aparece cano
   * flutuando com ceu por cima, por mais alta que seja a tela.
   */
  private layoutPipe(pipe: Pipe): void {
    const topEnd = Math.max(this.view.top + 8, pipe.gapY - PIPE_GAP / 2);
    const bottomY = pipe.gapY + PIPE_GAP / 2;

    pipe.topBody.setPosition(pipe.x, this.view.top).setSize(PIPE_WIDTH, topEnd - this.view.top);
    pipe.topCap.setPosition(pipe.x, topEnd);
    pipe.bottomBody.setPosition(pipe.x, bottomY).setSize(PIPE_WIDTH, GROUND_Y - bottomY);
    pipe.bottomCap.setPosition(pipe.x, bottomY);
  }

  private destroyPipe(pipe: Pipe): void {
    pipe.topBody.destroy();
    pipe.topCap.destroy();
    pipe.bottomBody.destroy();
    pipe.bottomCap.destroy();
  }

  private checkCollisions(): boolean {
    if (this.birdY + BIRD_HALF >= GROUND_Y) return true;

    for (const pipe of this.pipes) {
      const withinX =
        this.birdX + BIRD_HALF > pipe.x - PIPE_WIDTH / 2 &&
        this.birdX - BIRD_HALF < pipe.x + PIPE_WIDTH / 2;
      if (!withinX) continue;

      const gapTop = pipe.gapY - PIPE_GAP / 2;
      const gapBottom = pipe.gapY + PIPE_GAP / 2;
      if (this.birdY - BIRD_HALF < gapTop || this.birdY + BIRD_HALF > gapBottom) return true;
    }
    return false;
  }
}
